import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAuth } from '../../../hooks/useAuth';
import { IRootState } from '../../../store/reducer';
import { isNflPropsDevBypass } from '../data/leagueConfig';

interface AuthCheckResponse {
  result: boolean;
  userId?: string;
  authTier?: string;
}

/** Minimum gap between tab-return rechecks while locked out (the backend caches ~10 min anyway). */
const RECHECK_MIN_INTERVAL_MS = 30 * 1000;

/**
 * Does the signed-in user have a Props subscription for the selected league?
 *
 * This drives PRESENTATION only (full tool vs. teaser + upsell). Enforcement is
 * server-side: the stokastic-site proxy strips the board for non-subscribers
 * regardless of what this hook says.
 *
 * `hasActiveSubscription` is three-state: true / false / null (unknown, or not
 * signed in). `checkFailed` is set when the check itself errored, so the UI can
 * offer a retry instead of selling to a possibly-paying user.
 *
 * A `recheck()` (button, or tab return while locked out) re-runs the request
 * WITHOUT resetting to the loading state, so the board stays mounted and only
 * changes if the answer changes.
 */
export function useSubscriptionCheck() {
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth();
  const selectedLeague = useSelector((state: IRootState) => state.stokasticProps.filters.league);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState<boolean | null>(null);
  const [checkFailed, setCheckFailed] = useState(false);
  // Track which league the subscription was checked for to prevent race conditions
  const [checkedLeague, setCheckedLeague] = useState<string | null>(null);
  // Bumped by recheck() to re-run the effect without changing its other inputs
  const [recheckNonce, setRecheckNonce] = useState(0);
  // Inputs of the last run: a run with the same inputs is a recheck, not a fresh check
  const lastInputsRef = useRef<string | null>(null);
  const lastCheckedAtRef = useRef(0);

  const userEmail = user?.email;

  useEffect(() => {
    const abortController = new AbortController();

    const checkSubscription = async () => {
      // Skip if not authenticated or still loading auth
      if (isAuthLoading || !isAuthenticated) {
        setHasActiveSubscription(null);
        setCheckedLeague(null);
        setCheckFailed(false);
        // An in-flight check aborted by this transition skips its `finally`; clear the flag here
        // so a sign-out mid-check can't leave the tool on the spinner.
        setIsCheckingSubscription(false);
        lastInputsRef.current = null;
        return;
      }

      // Signed in but no email claim: there is nothing to check against. Land on a terminal
      // failed state (retry available) rather than "unknown", which would spin forever.
      if (!userEmail) {
        console.error('Subscription check skipped: authenticated user has no email claim');
        setHasActiveSubscription(false);
        setCheckFailed(true);
        setCheckedLeague(selectedLeague);
        setIsCheckingSubscription(false);
        lastInputsRef.current = null;
        return;
      }

      // Dev-only: NFL mock / local-API modes have no Stripe product behind them; treat as subscribed.
      if (selectedLeague === 'NFL' && isNflPropsDevBypass()) {
        setHasActiveSubscription(true);
        setCheckFailed(false);
        setCheckedLeague('NFL');
        setIsCheckingSubscription(false);
        return;
      }

      const inputs = `${userEmail}|${selectedLeague}`;
      const isRecheck = lastInputsRef.current === inputs;
      lastInputsRef.current = inputs;

      // A fresh check (new league / user) shows the loading state so the previous league's
      // board never renders with the wrong access. A recheck keeps everything as-is.
      if (!isRecheck) {
        setHasActiveSubscription(null);
        setCheckedLeague(null);
        setIsCheckingSubscription(true);
      }
      try {
        const apiUrl = process.env.REACT_APP_API_URL;

        // Check for PROPS subscription for the selected sport
        const response = await fetch(
          `${apiUrl}/api/auth/stripe?email=${encodeURIComponent(userEmail)}&sport=${selectedLeague}&appName=PROPS`,
          { signal: abortController.signal }
        );

        if (response.ok) {
          const data: AuthCheckResponse = await response.json();
          setHasActiveSubscription(data.result === true);
          setCheckFailed(false);
          setCheckedLeague(selectedLeague);
        } else {
          setHasActiveSubscription(false);
          setCheckFailed(true);
          setCheckedLeague(selectedLeague);
        }
      } catch (error) {
        // Ignore abort errors from effect cleanup (stale request cancelled)
        if (abortController.signal.aborted) return;
        console.error('Failed to check subscription:', error);
        setHasActiveSubscription(false);
        setCheckFailed(true);
        setCheckedLeague(selectedLeague);
      } finally {
        if (!abortController.signal.aborted) {
          lastCheckedAtRef.current = Date.now();
          setIsCheckingSubscription(false);
        }
      }
    };

    checkSubscription();

    // Cleanup: abort in-flight request when dependencies change or component unmounts.
    // This prevents a stale response (e.g. for the old league) from overwriting
    // the state set by the current request, which caused an infinite loading spinner.
    return () => {
      abortController.abort();
    };
  }, [isAuthenticated, isAuthLoading, userEmail, selectedLeague, recheckNonce]);

  /** Re-run the check in place (e.g. "Just subscribed? Refresh access"). */
  const recheck = useCallback(() => {
    setRecheckNonce((n) => n + 1);
  }, []);

  // While locked out, re-check when the tab becomes visible again so a user returning from
  // checkout unlocks without a reload. Throttled; subscribed users never pay for this.
  // (`visibilitychange` covers every case `focus` would here, without firing twice.)
  useEffect(() => {
    if (hasActiveSubscription !== false) return undefined;
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastCheckedAtRef.current < RECHECK_MIN_INTERVAL_MS) return;
      recheck();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [hasActiveSubscription, recheck]);

  // Consider loading if auth is loading, subscription check is in progress,
  // subscription status is unknown (null), or if checked league doesn't match current league
  const isLoading = isAuthLoading || isCheckingSubscription ||
    (isAuthenticated && hasActiveSubscription === null) ||
    (isAuthenticated && checkedLeague !== null && checkedLeague !== selectedLeague);

  // A result only counts for the league it was checked for — true AND false — so a league
  // switch reports "unknown" (null) until the new check lands, never the old league's answer.
  const resultForCurrentLeague = checkedLeague === selectedLeague ? hasActiveSubscription : null;

  return {
    isLoading,
    isAuthenticated,
    hasActiveSubscription: resultForCurrentLeague,
    checkFailed,
    recheck,
    selectedLeague,
    checkedLeague,
  };
}
